import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, StepTransitionTiming, type StepTransitionTimingRow } from '@xxtract/ui';
import { formatFetchedAt, formatWorkdays, stepLabel } from '../format';
import { useRefreshStepTiming } from '../hooks/useProcessDetail';
import type { CanonicalProcessName, FunnelStep, StepTiming } from '../processes.api';
import type { ProcessFilterValues } from '../procesLijsten';

/**
 * Story 4.2 (FR11/FR21, UXR5) — vult het StepTransitionTiming-blok dat Story 1.2 als placeholder
 * opzette. AD-22: DIT blok toont zijn EIGEN "bijgewerkt {tijd}"-caption uit
 * `sources.procesManagementEvents.fetchedAt` (niet de page-level `fetchedAt`) en heeft een EIGEN
 * ververs-affordance die alleen zijn eigen (lange-TTL) cache-key invalideert — de funnel-ververs
 * elders op deze pagina triggert de zware `/events`-scan nooit. AD-23: bij
 * `procesManagementEvents.status === 'unavailable'` degradeert UITSLUITEND dit blok.
 */
export function StepTransitionTimingBlock({
  funnelSteps,
  name,
  filters,
  stepTimings,
  eventsSourceFetchedAt,
  eventsSourceUnavailable,
  allCustomers = false,
}: {
  funnelSteps: FunnelStep[];
  name: CanonicalProcessName;
  filters: ProcessFilterValues;
  stepTimings: StepTiming[];
  eventsSourceFetchedAt: string | undefined;
  eventsSourceUnavailable: boolean;
  /** Story 4: an active filter does not reach these times (the API does not filter them). */
  allCustomers?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshStepTiming = useRefreshStepTiming(name, filters);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshStepTiming();
    } finally {
      setIsRefreshing(false);
    }
  };

  const timingByStep = new Map(stepTimings.map((entry) => [entry.canonicalStep, entry]));
  // Elke funnel-stap krijgt een rij — een stap zonder eigen stepTimings-entry (0 waarnemingen in
  // het venster) is voor de UI ONDERSCHEIDEN van een expliciet gemeten `insufficient_data`
  // (dezelfde eerlijkheidsstate, andere oorzaak — de backend levert geen entry voor 0-observaties).
  const rows: StepTransitionTimingRow[] = funnelSteps.map((step) => {
    const timing = timingByStep.get(step.canonicalStep);
    return {
      canonicalStep: step.canonicalStep,
      label: stepLabel(step.canonicalStep, t),
      observations: timing?.observations ?? 0,
      meanWorkdaysSinceReceipt: timing?.state === 'measured' ? timing.meanWorkdaysSinceReceipt : null,
      spreadWorkdays: timing?.state === 'measured' ? timing.spreadWorkdays : null,
      state: timing?.state ?? 'insufficient_data',
    };
  });

  return (
    <Card data-testid="process-detail-step-timing">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>{t('processes.detail.stepTimingTitle')}</CardTitle>
        <Button data-testid="step-timing-refresh" disabled={isRefreshing} onClick={handleRefresh} size="sm" variant="secondary">
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]">refresh</span>
          {t('processes.refresh')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {eventsSourceFetchedAt != null ? (
          <p aria-live="polite" className="text-[11px] text-[var(--foreground-muted)]" data-testid="step-timing-updated-at">
            {t('processes.updatedAt', { time: formatFetchedAt(eventsSourceFetchedAt, i18n.language) })}
          </p>
        ) : null}

        {eventsSourceUnavailable ? (
          <p className="text-xs text-[var(--info-text)]" role="status">{t('processes.degraded.title')}</p>
        ) : (
          <>
            {allCustomers ? (
              <p className="text-[11px] text-[var(--info-text)]" data-testid="step-timing-all-customers">{t('processes.allCustomers.timing')}</p>
            ) : null}
            <StepTransitionTiming
              ariaLabel={t('processes.detail.stepTimingTitle')}
              formatSpread={(value) => t('processes.stepTiming.spreadValue', { value: formatWorkdays(value) })}
              formatWorkdays={(value) => t('processes.stepTiming.workdaysValue', { value: formatWorkdays(value) })}
              insufficientDataLabel={t('processes.stepTiming.insufficientData')}
              rows={rows}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
